/**
 * Interview detail editor — opened from the calendar.
 * Supports both bound interviews and unattached/placeholder slots:
 *   • Assign / change Candidate, Job, Interviewers right here
 *   • Quick contact buttons: Call / WhatsApp / Email when candidate has data
 *   • Start meeting (opens Google Meet link in a new tab)
 *   • Preview candidate (opens CandidateProfileDialog)
 *   • Mint Meet link, send invite, mark outcome/score
 *
 * Uses the generic `/api/calendar/interviews/{iid}` endpoint that works
 * regardless of whether the interview has a candidate_id yet.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Save, Video, MapPin, Send, Sparkles, ExternalLink, User2, Briefcase, ArrowRight, Trash2,
    Eye, Phone, Mail, MessageCircle, Search, X, UserPlus, Users as UsersIcon, Download,
} from "lucide-react";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUSES = ["Scheduled", "Completed", "Cancelled", "Rescheduled", "No-show"];
const OUTCOMES = ["", "Pass", "Fail", "Strong Hire", "Lean Hire", "Lean No", "No Hire", "Pending"];

function localFromIso(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
function isoFromLocal(local) {
    if (!local) return "";
    return new Date(local).toISOString();
}
function digitsOnly(s) { return (s || "").replace(/\D/g, ""); }

function CandidatePicker({ value, onChange, onSearchHit }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    const wrapRef = useRef(null);
    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            api.get("/calendar/quick-candidates", { params: { q, limit: 10 } })
                .then(({ data }) => setResults(data.items || []))
                .catch(() => setResults([]));
        }, 200);
        return () => timer.current && clearTimeout(timer.current);
    }, [q]);
    // Close on outside click + Escape
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
    }, [open]);
    return (
        <div className="relative" ref={wrapRef}>
            <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Search candidate by name, email, phone…"
                    className="rounded-none pl-8 pr-8"
                    data-testid="int-cand-search"
                />
                {(open || q) && (
                    <button
                        type="button"
                        onClick={() => { setOpen(false); setQ(""); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/50 hover:text-foreground"
                        data-testid="int-cand-close"
                        aria-label="Close"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            {open && results.length > 0 && (
                <div className="absolute z-30 mt-0.5 left-0 right-0 max-h-72 overflow-y-auto bg-white border border-border shadow-lg">
                    {results.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => { onChange(c); onSearchHit?.(c); setOpen(false); setQ(""); }}
                            className="w-full text-left px-3 py-2 hover:bg-secondary text-sm border-b border-border last:border-0"
                            data-testid={`int-cand-pick-${c.id}`}
                        >
                            <div className="font-medium">{c.name || "Unnamed"}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{c.email || c.phone || c.current_title || ""}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function JobPicker({ value, onChange }) {
    const [results, setResults] = useState([]);
    useEffect(() => {
        api.get("/calendar/quick-jobs", { params: { q: "", limit: 50 } })
            .then(({ data }) => setResults(data.items || []))
            .catch(() => setResults([]));
    }, []);
    return (
        <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? null : v)}>
            <SelectTrigger className="rounded-none h-10" data-testid="int-job-picker"><SelectValue placeholder="Pick a job" /></SelectTrigger>
            <SelectContent>
                <SelectItem value="_none">— No job —</SelectItem>
                {results.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}{j.status && j.status !== "active" ? ` · ${j.status}` : ""}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

function InterviewerMultiSelect({ interviewers, selectedIds, customNames, onChangeIds, onChangeCustom }) {
    const [customInput, setCustomInput] = useState("");
    const toggle = (id) => {
        if (selectedIds.includes(id)) onChangeIds(selectedIds.filter((x) => x !== id));
        else onChangeIds([...selectedIds, id]);
    };
    const addCustom = () => {
        const n = customInput.trim();
        if (!n) return;
        if (!customNames.includes(n)) onChangeCustom([...customNames, n]);
        setCustomInput("");
    };
    return (
        <div className="border border-border bg-white p-3 space-y-2">
            <div className="flex flex-wrap gap-1.5">
                {interviewers.length === 0 && customNames.length === 0 && selectedIds.length === 0 ? (
                    <span className="text-[11px] font-mono text-foreground/50">No interviewers added in /company. Manage them from there, or type custom names below.</span>
                ) : null}
                {interviewers.map((iv) => (
                    <button
                        key={iv.id}
                        type="button"
                        onClick={() => toggle(iv.id)}
                        className={`px-2 py-1 text-[11px] font-mono border ${selectedIds.includes(iv.id) ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                        data-testid={`int-iv-toggle-${iv.id}`}
                    >
                        {selectedIds.includes(iv.id) ? "✓ " : ""}{iv.name || iv.email}
                    </button>
                ))}
            </div>
            {customNames.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border">
                    {customNames.map((n) => (
                        <span key={n} className="px-2 py-1 text-[11px] font-mono border bg-amber-50 border-amber-300 inline-flex items-center gap-1" data-testid={`int-iv-custom-${n}`}>
                            {n}
                            <button type="button" onClick={() => onChangeCustom(customNames.filter((x) => x !== n))} className="text-rose-700 hover:text-rose-900"><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex items-center gap-1.5">
                <Input
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                    placeholder="Add external interviewer name…"
                    className="rounded-none h-8 text-sm"
                    data-testid="int-iv-custom-input"
                />
                <Button type="button" onClick={addCustom} variant="outline" className="rounded-none h-8 px-3" data-testid="int-iv-custom-add">
                    <UserPlus className="w-3 h-3" />
                </Button>
            </div>
        </div>
    );
}

export default function InterviewDetailDialog({ open, interview, onClose, onSaved, companyAddress = "" }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const [interviewers, setInterviewers] = useState([]);
    const [previewCid, setPreviewCid] = useState(null);
    const [candidateBound, setCandidateBound] = useState(null);   // updated when assigning new cand
    const [jobBound, setJobBound] = useState(null);

    useEffect(() => {
        if (interview) {
            setForm({
                round_name: interview.round_name || "",
                round_number: interview.round_number || 1,
                scheduled_at_local: localFromIso(interview.scheduled_at),
                duration_minutes: interview.duration_minutes || 30,
                online_offline: interview.online_offline || (interview.mode === "Onsite" ? "offline" : "online"),
                mode: interview.mode || "Video",
                location: interview.location || (interview.online_offline === "offline" ? companyAddress : ""),
                meet_link: interview.meet_link || "",
                status: interview.status || "Scheduled",
                outcome: interview.outcome || "",
                feedback: interview.feedback || "",
                score: interview.score || "",
                invite_sent_at: interview.invite_sent_at || "",
                candidate_id: interview.candidate_id || null,
                job_id: interview.job_id || null,
                interviewer_ids: interview.interviewer_ids || [],
                interviewer_names: (interview.interviewer_names || []).filter((n) => {
                    // separate "in-company" interviewer names from ad-hoc custom names — we keep both
                    return true;
                }),
            });
            setCandidateBound(interview.candidate || null);
            setJobBound(interview.job || null);
        } else {
            setForm(null); setCandidateBound(null); setJobBound(null);
        }
    }, [interview, companyAddress]);

    useEffect(() => {
        api.get("/company/interviewers").then(({ data }) => setInterviewers(data || [])).catch(() => setInterviewers([]));
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const cand = candidateBound || interview?.candidate || {};
    const job = jobBound || interview?.job || {};

    // Split interviewer_names into "from company" (matches interviewers list) and custom
    const { mappedFromIds, customNames } = useMemo(() => {
        if (!form) return { mappedFromIds: [], customNames: [] };
        const interviewerMap = new Map(interviewers.map((i) => [i.id, i.name || i.email]));
        const idsAsNames = (form.interviewer_ids || []).map((id) => interviewerMap.get(id) || id);
        const custom = (form.interviewer_names || []).filter((n) => !idsAsNames.includes(n));
        return { mappedFromIds: idsAsNames, customNames: custom };
    }, [form, interviewers]);

    if (!interview || !form) return null;

    const save = async () => {
        setSaving(true);
        try {
            // Rebuild interviewer_names = company-names from ids + custom names
            const interviewerMap = new Map(interviewers.map((i) => [i.id, i.name || i.email]));
            const namesFromIds = (form.interviewer_ids || []).map((id) => interviewerMap.get(id) || id);
            const allNames = [...namesFromIds, ...customNames];

            const payload = {
                round_name: form.round_name,
                round_number: Number(form.round_number) || 1,
                scheduled_at: isoFromLocal(form.scheduled_at_local) || null,
                duration_minutes: Number(form.duration_minutes) || null,
                online_offline: form.online_offline,
                mode: form.online_offline === "offline" ? "Onsite" : (form.mode || "Video"),
                location: form.location,
                meet_link: form.online_offline === "online" ? form.meet_link : "",
                status: form.status,
                outcome: form.outcome || null,
                feedback: form.feedback,
                score: form.score === "" ? null : Number(form.score),
                candidate_id: form.candidate_id,
                job_id: form.job_id,
                interviewer_ids: form.interviewer_ids || [],
                interviewer_names: allNames,
            };
            await api.patch(`/calendar/interviews/${interview.id}`, payload);
            toast.success("Interview saved");
            onSaved?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const generateMeet = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post(`/interviews/${interview.id}/meet-link`);
            set("meet_link", data.meet_link);
            set("online_offline", "online");
            toast.success(`Meet link generated${data.integration === "mock" ? " (mock)" : ""}`);
            onSaved?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setGenerating(false); }
    };

    const sendInvite = async () => {
        if (!form.scheduled_at_local) { toast.error("Set a date/time first"); return; }
        await save();
        setSending(true);
        try {
            const { data } = await api.post(`/interviews/${interview.id}/send-invite`, { note: "" });
            toast.success(`Invite sent${data.integration === "mock" ? " (mock)" : ""}`);
            set("invite_sent_at", data.sent_at);
            onSaved?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    const remove = async () => {
        if (!window.confirm("Delete this interview?")) return;
        try {
            await api.delete(`/calendar/interviews/${interview.id}`);
            toast.success("Interview deleted");
            onSaved?.(); onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const logContact = async (channel) => {
        try {
            await api.post("/audit/contact", { candidate_id: form.candidate_id, channel });
        } catch { /* swallow — purely audit */ }
    };

    const onCall = () => {
        if (!cand.phone) return;
        logContact("call");
        window.location.href = `tel:${cand.phone}`;
    };
    const onWa = () => {
        if (!cand.phone) return;
        const digits = digitsOnly(cand.phone);
        logContact("whatsapp");
        window.open(`https://wa.me/${digits}`, "_blank");
    };
    const onEmail = () => {
        if (!cand.email) return;
        logContact("email");
        window.location.href = `mailto:${cand.email}`;
    };
    const onStartMeeting = () => {
        if (!form.meet_link) return;
        window.open(form.meet_link, "_blank");
    };

    const assignCandidate = (c) => {
        set("candidate_id", c.id);
        setCandidateBound(c);
    };
    const unassignCandidate = () => {
        set("candidate_id", null);
        setCandidateBound(null);
    };

    const hasCandidate = !!form.candidate_id;
    const hasJob = !!form.job_id;

    return (
        <>
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="!p-0 !gap-0 w-screen sm:w-[96vw] max-w-none sm:max-w-3xl h-[100svh] sm:h-auto sm:max-h-[92vh] rounded-none sm:rounded-lg !inset-0 sm:!inset-auto !top-0 sm:!top-1/2 !left-0 sm:!left-1/2 !translate-x-0 sm:!translate-x-[-50%] !translate-y-0 sm:!translate-y-[-50%] [overflow:clip] !flex flex-col overscroll-contain" data-testid="interview-detail-dialog">
                <DialogTitle className="sr-only">Interview detail</DialogTitle>
                <DialogDescription className="sr-only">Edit interview time, mode, location, attendees and outcome.</DialogDescription>

                {/* HEADER — clean card layout */}
                <header className="px-5 py-4 pr-12 border-b border-border bg-secondary/40 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-ikb font-bold">{form.round_name || `Round ${form.round_number}`}</span>
                        {interview.is_placeholder && <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5">Unattached</span>}
                        {form.status && <span className="text-[10px] font-mono uppercase tracking-widest bg-white border border-border px-1.5 py-0.5">{form.status}</span>}
                        {interview.created_by_name && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 ml-auto" data-testid="int-recruiter">By <b className="text-foreground">{interview.created_by_name}</b></span>
                        )}
                    </div>

                    {/* Candidate card */}
                    <div className="bg-white border border-border p-3" data-testid="int-cand-card">
                        {hasCandidate && cand?.name ? (
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="w-9 h-9 bg-ikb text-white flex items-center justify-center font-mono text-sm font-bold shrink-0">
                                    {(cand.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <Link to={`/candidates/${form.candidate_id}`} target="_blank" className="font-display font-bold text-base hover:text-ikb inline-flex items-center gap-1 truncate" data-testid="int-cand-link">
                                        {cand.name} <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
                                    </Link>
                                    <div className="text-[11px] text-foreground/60 truncate">{cand.current_title || ""}{cand.email ? (cand.current_title ? " · " : "") + cand.email : ""}</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button type="button" onClick={() => setPreviewCid(form.candidate_id)} className="border border-border bg-white hover:bg-secondary p-1.5 inline-flex" title="Quick preview" data-testid="int-cand-preview"><Eye className="w-3.5 h-3.5" /></button>
                                    {cand.phone ? (
                                        <>
                                            <button type="button" onClick={onCall} className="border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 p-1.5 inline-flex" title={`Call ${cand.phone}`} data-testid="int-call"><Phone className="w-3.5 h-3.5" /></button>
                                            <button type="button" onClick={onWa} className="border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 p-1.5 inline-flex" title="WhatsApp" data-testid="int-wa"><WhatsAppIcon className="w-3.5 h-3.5" /></button>
                                        </>
                                    ) : (
                                        <span className="border border-border bg-secondary text-foreground/40 p-1.5 inline-flex cursor-not-allowed" title="No phone on file"><Phone className="w-3.5 h-3.5" /></span>
                                    )}
                                    {cand.email && (
                                        <button type="button" onClick={onEmail} className="border border-ikb bg-blue-50 text-ikb hover:bg-blue-100 p-1.5 inline-flex" title={`Email ${cand.email}`} data-testid="int-email"><Mail className="w-3.5 h-3.5" /></button>
                                    )}
                                    {form.meet_link && (
                                        <a href={form.meet_link} target="_blank" rel="noreferrer" onClick={onStartMeeting} className="border border-ikb bg-ikb text-white hover:bg-[#001f7a] px-2 py-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest" title="Start meeting" data-testid="int-start-meeting"><Video className="w-3 h-3" /> Start</a>
                                    )}
                                    <button type="button" onClick={unassignCandidate} className="border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 p-1.5 inline-flex" title="Unassign candidate" data-testid="int-cand-unassign"><X className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Assign candidate</Label>
                                <CandidatePicker onChange={assignCandidate} />
                                <div className="text-[10px] text-foreground/50 font-mono mt-1.5">Leave blank to keep this as an open slot.</div>
                            </div>
                        )}
                    </div>

                    {/* Job row */}
                    <div className="bg-white border border-border p-3 flex items-center gap-3 flex-wrap" data-testid="int-job-card">
                        <Briefcase className="w-4 h-4 text-foreground/50 shrink-0" />
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 shrink-0">Job</Label>
                        <div className="min-w-[240px] max-w-md flex-1">
                            <JobPicker value={form.job_id} onChange={(v) => set("job_id", v)} />
                        </div>
                        {hasJob && job?.title && (
                            <Link to={`/jobs/${form.job_id}`} target="_blank" className="border border-border bg-white p-1.5 hover:bg-secondary inline-flex items-center gap-1 text-[11px] font-mono" data-testid="int-job-link" title="Open job in new tab">
                                <Eye className="w-3.5 h-3.5" />
                            </Link>
                        )}
                        {hasJob && (
                            <button type="button" onClick={() => set("job_id", null)} className="border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 p-1.5 inline-flex" title="Unassign job" data-testid="int-job-unassign">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Round</Label>
                            <Input value={form.round_name} onChange={(e) => set("round_name", e.target.value)} placeholder="Phone screen / Tech Round 1 / …" className="rounded-none h-10" data-testid="int-round" />
                        </div>
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Round number</Label>
                            <Input type="number" min={1} value={form.round_number} onChange={(e) => set("round_number", e.target.value)} className="rounded-none h-10" data-testid="int-round-num" />
                        </div>
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">When</Label>
                            <Input type="datetime-local" value={form.scheduled_at_local} onChange={(e) => set("scheduled_at_local", e.target.value)} className="rounded-none h-10" data-testid="int-when" />
                        </div>
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Duration (minutes)</Label>
                            <Input type="number" min={5} step={5} value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="rounded-none h-10" data-testid="int-duration" />
                        </div>
                    </div>

                    {/* Interviewers multi-select */}
                    <div>
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Interviewers</Label>
                        <InterviewerMultiSelect
                            interviewers={interviewers}
                            selectedIds={form.interviewer_ids || []}
                            customNames={customNames}
                            onChangeIds={(ids) => set("interviewer_ids", ids)}
                            onChangeCustom={(names) => {
                                // Rebuild interviewer_names = [from-ids] + custom on save; we store custom only here
                                set("interviewer_names", [...(mappedFromIds || []), ...names]);
                            }}
                        />
                    </div>

                    {/* Online / Offline mode */}
                    <div className="border border-border bg-secondary/30 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-2">Mode</div>
                        <div className="inline-flex border border-border bg-white">
                            <button type="button" onClick={() => set("online_offline", "online")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${form.online_offline === "online" ? "bg-ikb text-white" : "text-foreground hover:bg-secondary"}`} data-testid="int-mode-online">
                                <Video className="w-3.5 h-3.5" /> Online
                            </button>
                            <button type="button" onClick={() => set("online_offline", "offline")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-l border-border ${form.online_offline === "offline" ? "bg-ikb text-white" : "text-foreground hover:bg-secondary"}`} data-testid="int-mode-offline">
                                <MapPin className="w-3.5 h-3.5" /> Offline
                            </button>
                        </div>

                        {form.online_offline === "online" ? (
                            <div className="mt-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <Input value={form.meet_link} onChange={(e) => set("meet_link", e.target.value)} placeholder="Google Meet / Zoom link…" className="rounded-none h-10 flex-1" data-testid="int-meet-link" />
                                    <Button type="button" onClick={generateMeet} disabled={generating} variant="outline" className="rounded-none h-10" data-testid="int-generate-meet">
                                        <Sparkles className="w-3.5 h-3.5 mr-1" /> {generating ? "…" : (form.meet_link ? "Re-mint" : "Mint Meet link")}
                                    </Button>
                                </div>
                                <p className="text-[10px] text-muted-foreground font-mono">Meet links are currently <strong>mocked</strong>. Wire Google OAuth in Settings → Integrations to mint real ones.</p>
                            </div>
                        ) : (
                            <div className="mt-3 space-y-2">
                                <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder={companyAddress || "Interview address (room / floor / building)…"} className="rounded-none h-10" data-testid="int-location" />
                                <button type="button" onClick={() => set("location", companyAddress)} disabled={!companyAddress} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 hover:bg-secondary disabled:opacity-40" data-testid="int-use-company-address">
                                    Use company address
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Status / outcome */}
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="rounded-none h-10" data-testid="int-status"><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Outcome</Label>
                            <Select value={form.outcome || "_none"} onValueChange={(v) => set("outcome", v === "_none" ? "" : v)}>
                                <SelectTrigger className="rounded-none h-10" data-testid="int-outcome"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_none">—</SelectItem>
                                    {OUTCOMES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Score (1-10)</Label>
                            <Input type="number" min={1} max={10} value={form.score} onChange={(e) => set("score", e.target.value)} className="rounded-none h-10" data-testid="int-score" />
                        </div>
                    </div>

                    <div>
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">Interview log / feedback</Label>
                        <textarea
                            value={form.feedback}
                            onChange={(e) => set("feedback", e.target.value)}
                            rows={4}
                            placeholder="Notes from the round — strengths, concerns, sample answers…"
                            className="w-full border border-border bg-white text-sm p-2 rounded-none outline-none focus:border-ikb"
                            data-testid="int-feedback"
                        />
                    </div>

                    {/* Per-interviewer assessments — populated by each
                        interviewer's lean session. Read-only summary. */}
                    {(interview?.interviewer_feedbacks || []).length > 0 && (
                        <CalendarAssessmentsBlock feedbacks={interview.interviewer_feedbacks} iid={interview.id} />
                    )}

                    {form.invite_sent_at && (
                        <div className="text-[11px] font-mono text-emerald-700">Invite sent · {new Date(form.invite_sent_at).toLocaleString()}</div>
                    )}
                </div>

                <footer className="border-t border-border bg-secondary/30 px-5 py-3 flex items-center gap-2 flex-wrap">
                    <Button onClick={save} disabled={saving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="int-save">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button type="button" onClick={sendInvite} disabled={sending || !hasCandidate} variant="outline" className="rounded-none" data-testid="int-send-invite" title={hasCandidate ? "" : "Assign a candidate first"}>
                        <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : (form.invite_sent_at ? "Resend invite" : "Send invite")}
                    </Button>
                    {interview?.id && form.candidate_id && (
                        <>
                            <a
                                href={`${API}/candidates/${form.candidate_id}/interviews/${interview.id}/invite.ics`}
                                className="rounded-none inline-flex items-center h-9 px-3 border border-border bg-white text-xs font-mono uppercase tracking-widest hover:bg-secondary"
                                data-testid="int-download-ics-candidate"
                                title="Download candidate .ics — hand it over before the automated email arrives"
                            >
                                <Download className="w-3.5 h-3.5 mr-1.5" /> .ics (candidate)
                            </a>
                            <a
                                href={`${API}/candidates/${form.candidate_id}/interviews/${interview.id}/invite.ics?variant=team`}
                                className="rounded-none inline-flex items-center h-9 px-3 border border-border bg-white text-xs font-mono uppercase tracking-widest hover:bg-secondary"
                                data-testid="int-download-ics-team"
                                title="Download team .ics — share with interviewers / add to your own calendar"
                            >
                                <Download className="w-3.5 h-3.5 mr-1.5" /> .ics (team)
                            </a>
                        </>
                    )}
                    <Button type="button" onClick={onClose} variant="ghost" className="rounded-none ml-auto" data-testid="int-close">Close</Button>
                    <Button type="button" onClick={remove} variant="ghost" className="rounded-none text-rose-700 hover:bg-rose-50" data-testid="int-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
                </footer>
            </DialogContent>
        </Dialog>

        {previewCid && (
            <CandidateProfileDialog
                open={!!previewCid}
                candidateId={previewCid}
                onClose={() => setPreviewCid(null)}
            />
        )}
        </>
    );
}

/* Compact, read-only per-interviewer assessment list for the calendar
   dialog. Mirrors the richer panel on the candidate Interviews tab but
   styled to fit inside the slim calendar drawer. */
function CalendarAssessmentsBlock({ feedbacks, iid }) {
    const [openId, setOpenId] = useState(feedbacks[0]?.user_id || null);
    return (
        <div className="border border-foreground/10" data-testid={`cal-assessments-${iid}`}>
            <div className="px-3 py-2 bg-foreground text-background flex items-center gap-2">
                <span className="font-display font-bold text-xs uppercase tracking-widest">Interviewer assessments</span>
                <span className="text-[10px] font-mono opacity-70 ml-auto">{feedbacks.length} submitted</span>
            </div>
            <div className="divide-y divide-border">
                {feedbacks.map((fb) => {
                    const open = openId === fb.user_id;
                    const rating = fb.rating_snapshot || {};
                    const qa = fb.qa_snapshot || [];
                    return (
                        <div key={fb.user_id} className="bg-white">
                            <button
                                type="button"
                                onClick={() => setOpenId(open ? null : fb.user_id)}
                                className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-secondary/40"
                                data-testid={`cal-assessment-toggle-${iid}-${fb.user_id}`}
                            >
                                <div className="w-6 h-6 bg-foreground text-background flex items-center justify-center font-mono text-[10px] font-bold">
                                    {(fb.user_name || "?").slice(0, 2).toUpperCase()}
                                </div>
                                <span className="text-xs font-medium flex-1 truncate">{fb.user_name}</span>
                                {fb.score != null && <span className="font-mono text-xs font-bold">{fb.score}/10</span>}
                                {fb.outcome && <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5">{fb.outcome}</span>}
                                {fb.closed_at && <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-400 bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Closed</span>}
                                <span className="text-foreground/40 text-xs">{open ? "−" : "+"}</span>
                            </button>
                            {open && (
                                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-border bg-secondary/20 text-xs" data-testid={`cal-assessment-detail-${iid}-${fb.user_id}`}>
                                    {fb.feedback ? (
                                        <p className="whitespace-pre-wrap bg-white border border-border p-2">{fb.feedback}</p>
                                    ) : (
                                        <p className="text-[11px] text-foreground/40 italic">No written feedback.</p>
                                    )}
                                    {(rating.overall != null || rating.recommendation) && (
                                        <div className="flex items-center gap-3 font-mono">
                                            {rating.overall != null && <span>Overall: <strong>{rating.overall}/10</strong></span>}
                                            {rating.recommendation && <span>Rec: <strong>{rating.recommendation}</strong></span>}
                                        </div>
                                    )}
                                    {qa.length > 0 && (
                                        <details className="text-xs">
                                            <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-foreground/60">Q&amp;A ({qa.length})</summary>
                                            <div className="space-y-1.5 mt-1.5">
                                                {qa.map((q, i) => (
                                                    <div key={i} className="border border-border p-2 bg-white">
                                                        <div className="font-semibold">Q{(q.order ?? i) + 1}. {q.question}</div>
                                                        {q.answer ? <div className="mt-1 text-foreground/80 whitespace-pre-wrap">{q.answer}</div> : <div className="mt-1 text-foreground/40 italic">(no answer)</div>}
                                                        {q.score != null && <div className="mt-1 text-[10px] font-mono">Score: {q.score}/10</div>}
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
